import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import { reportDocumentModelV1Schema, reportRevisionReferenceV1Schema,
  type EvidenceValue, type JsonValue, type ReportDocumentBlockV1, type ReportDocumentModelV1 } from '../../types/reportContractsV2';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { hashReportDocumentModelContent } from '../reportGeneration/reportDocumentIdentity';
import { admitReportSources } from '../reportGeneration/reportEvidenceSourceAdmission';
import { scrubInternalSentinelsDeep } from '../reportRendering/scrubInternalSentinels';
import { REPORT_LEGAL_DISCLAIMER_PARAGRAPHS } from '../reportLegalDisclaimer';
import { reportDemonstrationDisclosure } from '../demonstration/disclosure';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { REPORT_SUMMARY_BUILDER_VERSION, REPORT_SUMMARY_SCHEMA_VERSION, hashReportSummaryModel,
  reportSummaryModelV1Schema, type ReportSummaryEntry, type ReportSummaryModelV1 } from './types';

// Availability must see invalid zero/out-of-range ordinals too. The general
// citation collector deliberately omits zero, which is unsuitable at this
// immutable summary source-admission boundary.
const referencesIn = (value: string): number[] => [...new Set(
  [...value.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))]
    .flatMap(token => (token[0].match(/\d+/g) ?? []).map(Number)),
)];
const referencesFromIds = (ids: readonly string[]): number[] => ids.flatMap(id => {
  const match = /^(?:ref:|citation-ref-)(\d+)$/.exec(id); return match ? [Number(match[1])] : [];
});
const humanize = (value: string): string => value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ');
// Hard line breaks occupy readable page height even in a short source unit.
// Blank paragraphs also add spacing. Charge the complete unit and heading
// before admitting it;
// native geometry validation remains the final fit check.
const wholeEntrySelectionCost = (text: string, title: string): number =>
  text.length + (text.match(/\r\n|\r|\n/g)?.length ?? 0) * 50
    + (text.match(/\n\s*\n/g)?.length ?? 0) * 100 + title.length + 100;
const valueText = (value: JsonValue): string => {
  if (value == null) return 'Not recorded';
  if (typeof value !== 'object') return String(value);
  if (Array.isArray(value)) return value.map(valueText).join('; ');
  return Object.entries(value).map(([key, child]) => `${humanize(key)}: ${valueText(child)}`).join('; ');
};
const evidenceText = (value: EvidenceValue): string => {
  const content = value.state === 'known' ? valueText(value.value)
    : value.state === 'conflict' ? `Conflicting values: ${value.candidates.map(valueText).join(' / ')}. ${value.reason}`
      : `${value.state === 'unknown' ? 'Unknown' : 'Not applicable'}: ${value.reason}`;
  return [content, value.unit, value.confidence == null ? '' : `Confidence ${value.confidence}`, value.retrievedAt ? `Retrieved ${value.retrievedAt}` : ''].filter(Boolean).join(' · ');
};

/** Complete source units only: no sentence cuts, computed figures or omitted caveats. */
function fragments(block: ReportDocumentBlockV1): Array<{ itemId?: string; text: string; evidenceRefs?: number[] }> {
  switch (block.kind) {
    case 'narrative': return block.markdown.trim() ? [{ text: block.markdown }] : [];
    case 'legal': return [];
    case 'list': return block.items.filter(item => item.text.trim()).map(item => ({ itemId: item.id, text: item.text }));
    case 'kpi': return block.items.map(item => ({ itemId: item.id, text: `${item.label}: ${evidenceText(item.value)}`, evidenceRefs: referencesFromIds(item.value.sourceIds) }));
    case 'table': return block.rows.map(row => ({ itemId: row.id, text: [block.caption,
      ...block.columns.map(column => `${column.label}: ${row.cells[column.id] ? evidenceText(row.cells[column.id]) : 'Not recorded'}`)].filter(Boolean).join('\n'),
      evidenceRefs: referencesFromIds(Object.values(row.cells).flatMap(cell => cell.sourceIds)) }));
    case 'structured-data': return Array.isArray(block.value)
      ? block.value.map((value, index) => ({ itemId: `${block.id}:item:${index}`, text: valueText(value) }))
      : [{ text: valueText(block.value) }];
    default: { const exhaustive: never = block; throw new Error(`Unsupported source block ${String(exhaustive)}`); }
  }
}

/** Derive a bounded selection exclusively from the immutable canonical source. */
export function buildReportSummaryModel(frozenReport: SiteRagReport, sourceDocument: ReportDocumentModelV1): ReportSummaryModelV1 {
  const revision = reportRevisionReferenceV1Schema.parse(frozenReport.reportRevision);
  const original = reportDocumentModelV1Schema.parse(sourceDocument);
  if (frozenReport._metadataOnly || frozenReport.generationTier === 'draft' || frozenReport.isDraft
    || original.reportId !== frozenReport.id || original.reportType !== frozenReport.reportType
    || original.siteId !== frozenReport.siteId || original.revisionId !== revision.revisionId
    || original.contentHash !== revision.documentHash || hashReportDocumentModelContent(original) !== original.contentHash
    || hashCanonicalJson(original.demonstrationDisclosure ?? null) !== hashCanonicalJson(reportDemonstrationDisclosure(frozenReport) ?? null)) {
    throw new Error('A summary requires the complete, exact saved canonical report revision.');
  }
  // Project historical operational sentinels through the same maintained reader
  // hygiene, without recomputing or changing the original source identity.
  const document = scrubInternalSentinelsDeep(original);
  const originalBlocks = new Map(original.sections.flatMap(section => section.blocks.map(block => [block.id, block] as const)));
  const sourceRows = new Map(admitReportSources(frozenReport).map(row => [row.index, row]));
  const pages: ReportSummaryModelV1['pages'] = [
    { id: 'overview', title: 'Decision basis', entries: [] },
    { id: 'findings', title: 'Selected findings', entries: [] },
    { id: 'actions-sources', title: 'Actions, evidence gaps and sources', entries: [] },
  ];
  const budgets = [3_100, 5_300, 2_100];
  const gaps: ReportSummaryModelV1['gaps'] = document.omissions.map(omission => ({
    sectionId: omission.sectionId, title: document.sections.find(section => section.sectionId === omission.sectionId)?.title ?? omission.sectionId,
    reason: omission.reason, detail: omission.detail,
  }));
  const omittedSelections: ReportSummaryModelV1['omittedSelections'] = [];
  const selectedRefs = new Set<number>();
  const sourceLength = (index: number): number => {
    const reference = sourceRows.get(index)?.reference;
    return reference ? [reference.source, reference.field, reference.url, reference.retrievedAt,
      reference.qualification, reference.sourceGrade, reference.dataAsOf, ...(reference.subItems ?? []).flatMap(row => [row.topic, row.detail])]
      .filter(Boolean).join(' · ').length + 80 : 0;
  };
  const sourcesFit = (indices: readonly number[]): boolean => [...new Set([...selectedRefs, ...indices])].reduce((total, index) => total + sourceLength(index), 0) <= 1_800;
  const gapKeys = new Set(gaps.map(gap => `${gap.sectionId}:${gap.reason}:${gap.detail}`));
  const recordGap = (sectionId: string, title: string, reason: string, detail: string): void => {
    const key = `${sectionId}:${reason}:${detail}`;
    if (!gapKeys.has(key)) { gaps.push({ sectionId, title, reason, detail }); gapKeys.add(key); }
  };
  // A standfirst is one source unit. Never truncate a qualifier to fit or cite
  // an unavailable reference while silently presenting its claim as supported.
  const summaryRefs = referencesIn(document.summary);
  const missingSummaryRefs = summaryRefs.filter(index => !sourceRows.get(index)?.admitted);
  let summary = document.summary;
  if (missingSummaryRefs.length) {
    summary = '';
    recordGap('summary', 'Saved overview', 'source-unavailable', 'The saved overview cites an absent or unavailable source and is omitted from this selection. Its original claim and source gap remain in the full report.');
  } else if (summary.length > 1_200 || !sourcesFit(summaryRefs)) {
    summary = '';
    omittedSelections.push({ sectionId: 'summary', title: 'Saved overview', count: 1, reason: 'The complete overview remains in the full report; no qualifier was cut to fit this selection.' });
  } else {
    summaryRefs.forEach(index => selectedRefs.add(index));
  }
  // This optional saved field is outside canonical block iteration. It needs
  // the same exact source availability and complete-selection policy; private
  // frozen recommendations are never rewritten or supplied a fabricated ref.
  let recommendation = typeof frozenReport.conclusion?.recommendation === 'string' ? frozenReport.conclusion.recommendation : undefined;
  if (recommendation) {
    const recommendationRefs = referencesIn(recommendation);
    if (recommendationRefs.some(index => !sourceRows.get(index)?.admitted)) {
      recommendation = undefined;
      recordGap('recommendation', 'Report recommendation', 'source-unavailable', 'The saved recommendation cites an absent or unavailable source and is omitted from this selection. Its source gap remains unresolved in the complete saved report.');
    } else if (!sourcesFit(recommendationRefs)) {
      recommendation = undefined;
      omittedSelections.push({ sectionId: 'recommendation', title: 'Report recommendation', count: 1,
        reason: 'The complete recommendation and its qualified sources remain in the full report; no qualification was cut to fit this selection.' });
    } else recommendationRefs.forEach(index => selectedRefs.add(index));
  }
  const qualityTextLength = [...original.qualityDecision.findings, ...revision.deliveryDecision.reasons.map(reason => reason.message), original.demonstrationDisclosure?.notice ?? ''].join(' ').length;
  budgets[0] = Math.max(0, 2_900 - summary.length - qualityTextLength - (recommendation?.length ?? 0));
  const sourceLegal = document.sections.filter(section => section.role === 'legal').flatMap(section => section.blocks
    .flatMap(block => block.kind === 'legal' ? [block.text] : block.kind === 'narrative' ? [block.markdown] : [])).filter(Boolean);
  // The maintained report notice is platform wording, never evidence or a new
  // finding. Older canonical documents omit renderer-owned legal furniture.
  const legalNotice = sourceLegal.length ? sourceLegal : [...REPORT_LEGAL_DISCLAIMER_PARAGRAPHS];
  for (const section of document.sections) {
    if (section.role === 'legal' || section.group === 'evidence') continue;
    const pageIndex = section.group === 'overview' ? 0 : section.group === 'risks-actions' ? 2 : 1;
    const candidates = section.blocks.flatMap(block => fragments(block).map(fragment => ({ block, fragment })));
    let skipped = 0;
    for (const { block, fragment } of candidates) {
      const referenced = [...new Set([...referencesIn(fragment.text), ...referencesFromIds(block.citationIds),
        ...referencesFromIds(block.sourceIds), ...(fragment.evidenceRefs ?? [])])];
      const unavailable = referenced.filter(index => !sourceRows.get(index)?.admitted);
      if (unavailable.length) {
        skipped += 1;
        recordGap(section.sectionId, section.title, 'source-unavailable',
          `A selected source passage was omitted because its original reference ${unavailable.join(', ')} is absent or unavailable. Consult the full report and resolve that source gap before relying on the claim.`);
        continue;
      }
      // Complete entries only. A long source cannot be made stronger by cutting
      // a limitation or changing numeric precision to fit the page budget.
      const selectionCost = wholeEntrySelectionCost(fragment.text, section.title);
      if (selectionCost > budgets[pageIndex]! || !sourcesFit(referenced)
        || pages[pageIndex]!.entries.length >= (pageIndex === 1 ? 6 : 4)) { skipped += 1; continue; }
      const entry: ReportSummaryEntry = {
        id: `summary:${section.sectionId}:${block.id}:${fragment.itemId ?? 'complete'}`,
        sectionId: section.sectionId, sectionTitle: section.title, sourceBlockId: block.id,
        ...(fragment.itemId ? { sourceItemId: fragment.itemId } : {}),
        sourceContentHash: hashCanonicalJson(originalBlocks.get(block.id)),
        text: fragment.text, referenceIndices: referenced,
      };
      pages[pageIndex]!.entries.push(entry);
      budgets[pageIndex] = budgets[pageIndex]! - selectionCost;
      referenced.forEach(index => selectedRefs.add(index));
    }
    if (skipped) omittedSelections.push({ sectionId: section.sectionId, title: section.title, count: skipped,
      reason: 'Only complete source entries that fit this three-page selection are included; the full report retains the remaining detail.' });
  }
  const sources: ReportSummaryModelV1['sources'] = [...selectedRefs].sort((a, b) => a - b).flatMap(index => {
    const row = sourceRows.get(index);
    if (!row?.admitted || !row.reference.source.trim()) return [];
    const ref = row.reference;
    return [{ index, label: ref.source, retrievedAt: ref.retrievedAt, field: ref.field,
      ...(ref.url ? { url: ref.url } : {}), ...(ref.documentId ? { documentId: ref.documentId } : {}),
      ...(ref.pageNumber != null ? { pageNumber: ref.pageNumber } : {}),
      ...(ref.qualification ? { qualification: ref.qualification } : {}), ...(ref.sourceGrade ? { sourceGrade: ref.sourceGrade } : {}),
      ...(ref.dataAsOf ? { dataAsOf: ref.dataAsOf } : {}), scopeDetails: ref.subItems?.map(item => ({ ...item })) ?? [] }];
  });
  const model: Omit<ReportSummaryModelV1, 'modelHash'> = {
    schemaVersion: REPORT_SUMMARY_SCHEMA_VERSION, builderVersion: REPORT_SUMMARY_BUILDER_VERSION,
    identity: { reportId: original.reportId, revisionId: original.revisionId, sourceDocumentHash: original.contentHash,
      reportType: original.reportType, ...(original.siteId ? { siteId: original.siteId } : {}), siteName: original.siteName,
      address: original.address, sourceGeneratedAt: original.generatedAt, sourceVersion: original.sourceVersion },
    title: `${REPORT_TYPE_REGISTRY[original.reportType].shortLabel} · Summary`, reportTypeLabel: REPORT_TYPE_REGISTRY[original.reportType].label,
    // A complete canonical standfirst; no generated substitute for a missing one.
    summary,
    ...(recommendation !== undefined ? { recommendation } : {}),
    qualityDecision: original.qualityDecision, deliveryDecision: revision.deliveryDecision,
    ...(original.demonstrationDisclosure ? { demonstrationDisclosure: original.demonstrationDisclosure } : {}),
    pages, sources, gaps, omittedSelections, legalNotice,
  };
  return reportSummaryModelV1Schema.parse({ ...model, modelHash: hashReportSummaryModel(model) });
}

/** Recompute the complete projection; a valid self-hash alone is not authority. */
export function validateReportSummaryModel(value: unknown, frozenReport: SiteRagReport, frozenDocument: ReportDocumentModelV1): ReportSummaryModelV1 {
  const model = reportSummaryModelV1Schema.parse(value);
  const expected = buildReportSummaryModel(frozenReport, frozenDocument);
  if (model.modelHash !== hashReportSummaryModel(model) || hashCanonicalJson(model) !== hashCanonicalJson(expected)) {
    throw new Error('The summary does not match its exact saved report revision.');
  }
  return model;
}
